<template>
    <v-card>
        <v-card-title primary-title> Create Registry Schema </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="3">
                    <v-text-field :disabled="isEditRegistrySchema" label="Name" v-model="name" />
                </v-col>
                <v-col cols="3">
                    <v-text-field label="Display Name" v-model="displayName" />
                </v-col>
                <v-col cols="3">
                    <v-text-field label="ID Prefix" v-model="entryIdPrefix"
                                  :rules="requiredEntryIdPrefix('ID Prefix')" />
                </v-col>
                <v-col cols="2">
                    <v-switch label="Benchling?" class="mb-n5 mt-1" v-model="isBenchling" />
                </v-col>
                <v-col cols="2" v-if="isBenchling">
                    <v-switch label="Benchling Dropdown?" class="mb-n5 mt-1" v-model="isBenchlingDropdown" />
                </v-col>
                <v-col cols="2" v-if="isBenchling">
                    <v-text-field label="Benchling Registry name" v-model="benchlingRegistryName" />
                </v-col>
                <v-col cols="2" v-if="isBenchling">
                    <v-text-field label="Entry ID Suffix" v-model="benchlingRegistryEntryIdSuffix" />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="2">
                    <v-autocomplete label="Experiment Relationship"
                              :items="DEFAULT_EXPERIMENT_SETUP_CONFIG_TYPES"
                              v-model="experimentRelationshipType" />
                </v-col>
                <v-col cols="3">
                    <v-switch label="Treatment Component?" class="mb-n5 mt-1"
                              v-model="isTreatmentRegistry" />
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="cancel"> Cancel </v-btn>
            <v-btn color="primary" @click="saveRegistrySchema"> Save </v-btn>
        </v-card-actions>
    </v-card>
</template>
<script setup lang="ts">
import { ref, computed } from "vue";
import _ from "lodash";
import { withLoadingScreen } from "@/lib/loading";
import { registriesAPI, RegistrySchema, SavedRegistrySchema } from "@/services/registries/registriesAPI";
import { DEFAULT_EXPERIMENT_SETUP_CONFIG_TYPES } from "@/services/experimentRelationships/models";
import { transformEmptyStringToNull } from "@/lib/utils";
import { requiredTextValueRules } from "@/lib/validationUtils";

const { registrySchema } = defineProps<{
    registrySchema?: RegistrySchema
}>();

const emit = defineEmits<{
    (e: 'cancelForm' ): void,
    (e: 'registrySchemaSaved', savedSchema: SavedRegistrySchema);
}>()

const name = ref(registrySchema ? registrySchema.name : "");
const displayName = ref( registrySchema ? registrySchema.displayName : "");
const isBenchling = ref( registrySchema ? registrySchema.isBenchling : false);
const isBenchlingDropdown = ref( registrySchema ? registrySchema.isBenchlingDropdown : false);
const benchlingRegistryName = ref<string | null>( registrySchema ? registrySchema.benchlingRegistryName : null);
const benchlingRegistryEntryIdSuffix = ref<string | null>(registrySchema ? registrySchema.benchlingRegistryEntryIdSuffix : null);
const experimentRelationshipType = ref<string | null | undefined>( registrySchema ? registrySchema.registryExperimentRelationshipType : undefined);
const isTreatmentRegistry = ref( registrySchema ? registrySchema.isTreatmentRegistry : false);
const entryIdPrefix = ref<string | null>(registrySchema ? registrySchema.entryIdPrefix : "");

const isEditRegistrySchema = computed(() => {
    return !_.isNil(registrySchema);
})

function cancel() {
    emit("cancelForm");
}

function getBenchlingRegistryName(): string | null {
    return transformEmptyStringToNull(benchlingRegistryName.value);
}

function getBenchlingRegistryEntryIdSuffix() {
    return transformEmptyStringToNull(benchlingRegistryEntryIdSuffix.value);
}

function requiredEntryIdPrefix(fieldName: string) {
    if (_.isNil(registrySchema)) {
        return requiredTextValueRules(fieldName);
    } else {
        return [() => true];
    }
}

async function saveRegistrySchema() {

    await withLoadingScreen(async () => {
        const savedSchema = await registriesAPI.updateRegistrySchema({
            id: registrySchema ? registrySchema.id : undefined,
            name: name.value,
            displayName: displayName.value,
            isBenchling: isBenchling.value,
            isBenchlingDropdown: isBenchling.value && isBenchlingDropdown.value,
            attributes: registrySchema ? registrySchema.attributes : [],
            registryExperimentRelationshipType: experimentRelationshipType.value || null,
            isTreatmentRegistry: isTreatmentRegistry.value,
            benchlingRegistryName: getBenchlingRegistryName(),
            benchlingRegistryEntryIdSuffix: getBenchlingRegistryEntryIdSuffix(),
            entryIdPrefix: entryIdPrefix.value || null,
        });


        emit("registrySchemaSaved", savedSchema);
    });
}
</script>
